import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../../app/store'
import brandManager from '../../services/brandManager'
import { buildEndpoint, getPlatformHeader } from '../../utils/apiUtils'
import { getAuthToken } from '../../utils/secureStorage'

// Types for customer operations
export interface Customer {
  id: number
  name: string
  phone: string
  email?: string
  addresses?: CustomerAddress[]
  created_at?: string
  updated_at?: string
}

export interface CustomerAddress {
  id: number
  customer_id: number
  title: string
  address: string
  latitude?: number
  longitude?: number
  is_default?: boolean
  created_at?: string
  updated_at?: string
}

export interface SearchCustomerRequest {
  phone: string
}

export interface SearchCustomerResponse {
  success: boolean
  data: Customer[]
  message?: string
}

export interface CreateCustomerRequest {
  name: string
  phone: string
  password?: string
  pushToken?: string
  countryId?: number
  email?: string
}

export interface CreateCustomerResponse {
  success: boolean
  data: Customer
  message?: string
}

export interface UpdateCustomerRequest {
  id: number
  name?: string
  phone?: string
  email?: string
}

export interface UpdateCustomerResponse {
  success: boolean
  data: Customer
  message?: string
}

export interface GetCustomerAddressesRequest {
  customer_id: number
}

export interface GetCustomerAddressesResponse {
  success: boolean
  data: CustomerAddress[]
  message?: string
}

export interface CreateCustomerAddressRequest {
  customer_id: number
  title: string
  address: string
  latitude?: number
  longitude?: number
  is_default?: boolean
}

export interface CreateCustomerAddressResponse {
  success: boolean
  data: CustomerAddress
  message?: string
}

// Customer API
export const customerApi = createApi({
  reducerPath: 'customerApi',
  baseQuery: fetchBaseQuery({
    baseUrl: brandManager.apiUrl,
    prepareHeaders: (headers, { getState }) => {
      // Try to get token from Redux state first
      let token = (getState() as RootState).auth.token
      
      // If not in Redux state (e.g., after page refresh before rehydration), get from secure storage
      if (!token) {
        token = getAuthToken()
      }
      
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }
      // Add language header
      const lang = localStorage.getItem('language') || 'ar'
      headers.set('lang', lang)
      headers.set('platform', getPlatformHeader())
      headers.set('Content-Type', 'application/json')
      headers.set('Accept', 'application/json')
      return headers
    },
  }),
  tagTypes: ['Customer', 'CustomerAddress'],
  endpoints: (builder) => ({
    // Search customer by phone number - uses cashier or customerService API endpoint
    searchCustomerByPhone: builder.query<SearchCustomerResponse, SearchCustomerRequest>({
      query: ({ phone }) => ({
        url: buildEndpoint('/cashier/users/listByPhone'),
        method: 'GET',
        params: { phone },
      }),
      providesTags: ['Customer'],
    }),

    // Get customer by ID
    getCustomerById: builder.query<{ success: boolean; data: Customer }, number>({
      query: (id) => `/customers/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Customer', id }],
    }),

    // Create new customer - uses cashier or customerService API endpoint
    createCustomer: builder.mutation<CreateCustomerResponse, CreateCustomerRequest>({
      query: (customer) => ({
        url: buildEndpoint('/cashier/users'),
        method: 'POST',
        body: customer,
      }),
      invalidatesTags: ['Customer'],
    }),

    // Update customer (equivalent to Flutter's updateCustomer)
    updateCustomer: builder.mutation<UpdateCustomerResponse, UpdateCustomerRequest>({
      query: ({ id, ...updates }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Customer', id }],
    }),

    // Get customer addresses (equivalent to Flutter's getCustomerAddresses)
    getCustomerAddresses: builder.query<GetCustomerAddressesResponse, GetCustomerAddressesRequest>({
      query: ({ customer_id }) => `/${customer_id}/addresses`,
      providesTags: (_result, _error, { customer_id }) => [
        { type: 'CustomerAddress', id: customer_id }
      ],
    }),

    // Create customer address (equivalent to Flutter's createCustomerAddress)
    createCustomerAddress: builder.mutation<CreateCustomerAddressResponse, CreateCustomerAddressRequest>({
      query: (address) => ({
        url: '/addresses',
        method: 'POST',
        body: address,
      }),
      invalidatesTags: (_result, _error, { customer_id }) => [
        { type: 'CustomerAddress', id: customer_id }
      ],
    }),

    // Update customer address
    updateCustomerAddress: builder.mutation<CreateCustomerAddressResponse, { id: number } & Partial<CustomerAddress>>({
      query: ({ id, ...updates }) => ({
        url: `/addresses/${id}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'CustomerAddress', id }
      ],
    }),

    // Delete customer address
    deleteCustomerAddress: builder.mutation<{ success: boolean; message?: string }, number>({
      query: (id) => ({
        url: `/addresses/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'CustomerAddress', id }
      ],
    }),
  }),
})

// Export hooks for usage in functional components
export const {
  useSearchCustomerByPhoneQuery,
  useGetCustomerByIdQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useGetCustomerAddressesQuery,
  useCreateCustomerAddressMutation,
  useUpdateCustomerAddressMutation,
  useDeleteCustomerAddressMutation,
} = customerApi
